import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Eye, Pencil, Trash2, XCircle } from "lucide-react";

import { StatusBadge } from "../../../components/ui/StatusBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { formatDateRange } from "../../../utils/dates";
import {
  CANCELLABLE_STATUSES,
} from "../../../utils/orderStatus";

/**
 * Icon button used in the actions column. Always rendered (visible even
 * when disabled) — greys out via CSS with an explanatory tooltip.
 */
function IconButton({
  icon: Icon,
  label,
  onClick,
  disabled,
  testId,
  tone = "neutral",
}) {
  const toneClasses = {
    neutral: "text-text-secondary hover:bg-offwhite hover:text-charcoal",
    danger:
      "text-status-unavailable hover:bg-status-unavailable/10 hover:text-status-unavailable",
    warning: "text-taupe hover:bg-offwhite hover:text-charcoal",
  }[tone];

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onClick();
              }}
              disabled={disabled}
              aria-label={label}
              data-testid={testId}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent ${toneClasses}`}
            >
              <Icon className="h-4 w-4" />
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] text-xs">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Returns { disabled, label, href } for the Edit icon on a given order. */
function editAffordance(order, t) {
  const cancellable = CANCELLABLE_STATUSES.has(order.status);
  if (order.status === "draft") {
    return { disabled: false, label: t("orders.row_actions.edit_draft"), href: `/orders/${order.id}/edit` };
  }
  if (!cancellable) {
    return {
      disabled: true,
      label: t("orders.row_actions.edit_blocked_dispatch"),
    };
  }
  return { disabled: false, label: t("orders.row_actions.edit"), href: `/orders/${order.id}/edit` };
}

/** Returns { disabled, label } for the Cancel icon on a given order. */
function cancelAffordance(order, t) {
  if (order.status === "cancelled") {
    return { disabled: true, label: t("orders.row_actions.cancel_blocked_cancelled") };
  }
  // Anything that already reached the customer / warehouse is "complete".
  if (["delivered", "collected", "quality_testing", "collection_complete"].includes(order.status)) {
    return { disabled: true, label: t("orders.row_actions.cancel_blocked_complete") };
  }
  if (!CANCELLABLE_STATUSES.has(order.status)) {
    return { disabled: true, label: t("orders.row_actions.cancel_blocked_status") };
  }
  return { disabled: false, label: t("orders.row_actions.cancel") };
}

export function OrdersTable({ orders, onCancel, onDeleteDraft, showUserColumn = false, viewOnly = false, hideOrderNumber = false }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  return (
    <div
      className="overflow-hidden rounded-lg border border-border-subtle bg-surface shadow-card"
      data-testid="orders-table-wrapper"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="orders-table">
          <thead className="bg-offwhite text-left text-[11px] uppercase tracking-wide text-text-secondary">
            <tr>
              {!hideOrderNumber && (
                <th className="px-4 py-3 font-semibold">
                  {t("orders.columns.order_number")}
                </th>
              )}
              {showUserColumn && (
                <th className="px-4 py-3 font-semibold" data-testid="orders-col-user">
                  {t("orders.columns.user")}
                </th>
              )}
              <th className="px-4 py-3 font-semibold">
                {t("orders.columns.event")}
              </th>
              <th className="px-4 py-3 font-semibold">
                {t("orders.columns.period")}
              </th>
              <th className="px-4 py-3 font-semibold">
                {t("orders.columns.status")}
              </th>
              <th className="px-4 py-3 text-right font-semibold">
                {t("orders.columns.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {orders.map((o) => {
              const edit = editAffordance(o, t);
              const cancel = cancelAffordance(o, t);
              // Admins have full edit/cancel/delete access on any order
              // (Refinement 6, backend enforced). Only standard users are
              // still blocked from mutating orders they don't own — and
              // the API returns 404 for them anyway, so we can leave the
              // status gates driving the icon disabled state.
              return (
                <tr
                  key={o.id}
                  onClick={() => navigate(`/orders/${o.id}`)}
                  data-testid={`order-row-${o.id}`}
                  className="cursor-pointer transition-colors hover:bg-offwhite/60"
                >
                  {!hideOrderNumber && (
                    <td className="px-4 py-3">
                      <span
                        className="font-mono text-sm font-semibold text-text-primary"
                        data-testid={`order-row-number-${o.id}`}
                      >
                        {o.order_number ? `#${o.order_number}` : ""}
                      </span>
                    </td>
                  )}
                  {showUserColumn && (
                    <td className="px-4 py-3 text-text-primary" data-testid={`order-row-user-${o.id}`}>
                      <span
                        className="font-medium"
                        title={o.user?.email || ""}
                      >
                        {o.user?.name || o.user?.email || "—"}
                      </span>
                    </td>
                  )}
                  <td
                    className="max-w-[320px] truncate px-4 py-3 text-text-primary"
                    title={o.event_name}
                  >
                    {o.event_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                    {formatDateRange(o.from_date, o.until_date, i18n.language)}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={o.status} size="sm" />
                  </td>
                  <td
                    className="px-4 py-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="inline-flex items-center gap-2">
                      <IconButton
                        icon={Eye}
                        label={t("orders.row_actions.view")}
                        onClick={() => navigate(`/orders/${o.id}`)}
                        testId={`order-action-view-${o.id}`}
                      />
                      {!viewOnly && (
                        <IconButton
                          icon={Pencil}
                          label={edit.label}
                          onClick={() => edit.href && navigate(edit.href)}
                          disabled={edit.disabled}
                          tone="warning"
                          testId={`order-action-edit-${o.id}`}
                        />
                      )}
                      {!viewOnly && (o.status === "draft" ? (
                        <IconButton
                          icon={Trash2}
                          label={t("orders.row_actions.delete_draft")}
                          onClick={() => onDeleteDraft && onDeleteDraft(o)}
                          tone="danger"
                          testId={`order-action-delete-${o.id}`}
                        />
                      ) : (
                        <IconButton
                          icon={XCircle}
                          label={cancel.label}
                          onClick={() => onCancel(o)}
                          disabled={cancel.disabled}
                          tone="danger"
                          testId={`order-action-cancel-${o.id}`}
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function OrdersTableSkeleton({ rows = 6 }) {
  return (
    <div
      className="overflow-hidden rounded-lg border border-border-subtle bg-surface"
      data-testid="orders-table-skeleton"
    >
      <div className="animate-pulse divide-y divide-border-subtle">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <div className="h-4 w-16 rounded bg-offwhite" />
            <div className="h-4 w-48 rounded bg-offwhite" />
            <div className="h-4 w-24 rounded bg-offwhite" />
            <div className="h-5 w-20 rounded-full bg-offwhite" />
            <div className="ml-auto h-6 w-24 rounded bg-offwhite" />
          </div>
        ))}
      </div>
    </div>
  );
}
